import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import {
  Button,
  Card,
  DateField,
  MonoLabel,
  Select,
  Skeleton,
  TextField,
  type SelectOption
} from '../primitives';
import { supabase } from '../../lib/supabase';
import { useOrgProjects } from '../../lib/projects/useOrgProjects';
import { useRealtimeRowReload } from '../../hooks/useRealtimeRowReload';
import {
  fetchTypePipelines,
  stagesFor,
  type PipelineKey
} from '../../lib/workItemPipelines';
import type { Database } from '../../types/database';

type WorkItemPropertyDefRow =
  Database['public']['Tables']['work_item_property_defs']['Row'];
type WorkItemPropertyValueRow =
  Database['public']['Tables']['work_item_property_values']['Row'];
type WorkItemType = Database['public']['Enums']['work_item_type'];

export interface PropertiesPanelProps {
  workItemId: string;
  orgId: string;
  workItemType: WorkItemType;
  workItemStatus: string;
  workItemDueDate: string | null;
  /** Only surfaced for Epics today. NULL for non-Epic types. */
  workItemStartDate: string | null;
  workItemEndDate: string | null;
  /** Stored `parent_work_item_id`. NULL when unset. */
  parentWorkItemId: string | null;
  parentName: string | null;
  /** Stored `project_id`. NULL when unassigned. */
  workItemProjectId: string | null;
  /**
   * Stored `work_items.build_order` — the item's 1-based position among its
   * SIBLINGS (the items sharing its parent; 05-project-hub-build-order scoped
   * the property per sibling group). NULL when unordered (sorts last).
   */
  workItemBuildOrder: number | null;
  onStatusChange: (status: string) => void;
  onDueDateChange: (next: string | null) => void;
  onStartDateChange: (next: string | null) => void;
  onEndDateChange: (next: string | null) => void;
  onParentChange: (next: string | null) => void;
  onProjectChange: (next: string | null) => void;
  onBuildOrderChange: (next: number | null) => void;
  onError: (message: string) => void;
  /**
   * Fired after any property-mutation RPC (status / dates / parent) lands
   * successfully. These RPCs write `audit_events` rows server-side, so the
   * parent uses this to reload the History drawer's Activity tab — without
   * it the just-made change never surfaces until an unrelated reload.
   */
  onActivityChange?: () => void;
  /**
   * When true the panel renders only its inner rows (plus the picker
   * footer) — the parent (typically `InspectorTabs`) supplies its own
   * chrome (border, padding, title). When omitted/false the panel wraps
   * itself in a `<Card>` with a "Properties" head + footer, matching the
   * legacy two-column layout.
   */
  naked?: boolean;
}

const TYPE_LABEL: Record<string, string> = {
  epic: 'Epic',
  story: 'Story',
  bug: 'Bug',
  task: 'Task',
  subtask: 'Subtask'
};

const TYPE_LOCK_TOOLTIP =
  'Type is locked at creation. To change the type, archive this and create a new work item.';

const COMMIT_DEBOUNCE_MS = 600;

const NO_EPIC_VALUE = '__no_epic__';
const NO_PROJECT_VALUE = '__no_project__';

interface EpicOption {
  id: string;
  name: string;
}

interface LoadedState {
  kind: 'ready';
  defs: WorkItemPropertyDefRow[];
  values: Map<string, WorkItemPropertyValueRow>;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | LoadedState;

export function PropertiesPanel({
  workItemId,
  orgId,
  workItemType,
  workItemStatus,
  workItemDueDate,
  workItemStartDate,
  workItemEndDate,
  parentWorkItemId,
  parentName,
  workItemProjectId,
  workItemBuildOrder,
  onStatusChange,
  onDueDateChange,
  onStartDateChange,
  onEndDateChange,
  onParentChange,
  onProjectChange,
  onBuildOrderChange,
  onError,
  onActivityChange,
  naked = false
}: PropertiesPanelProps) {
  const [load, setLoad] = useState<LoadState>({ kind: 'loading' });
  const [pickerOpen, setPickerOpen] = useState(false);
  const [statusStages, setStatusStages] = useState<ReadonlyArray<string>>([]);
  const [epicOptions, setEpicOptions] = useState<EpicOption[]>([]);
  const [dateError, setDateError] = useState<string | null>(null);

  // Org projects power the single-select Project picker. A child work item
  // inherits its parent's project (DB trigger), so its row is read-only.
  const { projects } = useOrgProjects(orgId);
  const isChildItem = parentWorkItemId !== null;
  const inheritedProjectName =
    projects.find((p) => p.id === workItemProjectId)?.name ?? null;

  // Defer a live refresh while the user's own commit is in flight (the Realtime
  // event fires for our own write too) so it never re-fetches mid-write and
  // briefly stomps the optimistic value.
  const pendingCommitsRef = useRef(0);
  const refreshQueuedRef = useRef(false);

  // Resolve the status dropdown options for this work item's pipeline.
  useEffect(() => {
    let cancelled = false;
    const pipelineKey: PipelineKey =
      workItemType === 'epic'
        ? 'epic'
        : workItemType === 'subtask'
        ? 'subtask'
        : null;
    void (async () => {
      const res = await fetchTypePipelines([pipelineKey], orgId);
      if (cancelled) return;
      setStatusStages(stagesFor(res.byType, pipelineKey));
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemType, orgId]);

  // Stories / Bugs / Tasks get an Epic picker — fetch the org's epics.
  useEffect(() => {
    if (workItemType === 'epic' || workItemType === 'subtask') {
      setEpicOptions([]);
      return;
    }
    let cancelled = false;
    void (async () => {
      const { data, error } = await supabase
        .from('work_items')
        .select('id, name')
        .eq('org_id', orgId)
        .eq('type', 'epic')
        .order('name', { ascending: true });
      if (cancelled) return;
      if (error) {
        onError(error.message);
        return;
      }
      setEpicOptions(
        (data ?? []).map((r) => ({
          id: r.id as string,
          name: (r.name as string) ?? ''
        }))
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemType, orgId, onError]);

  useEffect(() => {
    let cancelled = false;
    setLoad({ kind: 'loading' });
    void (async () => {
      const [defsRes, valsRes] = await Promise.all([
        supabase
          .from('work_item_property_defs')
          .select(
            'id, org_id, key, kind, label, description, default_value, position, type_id, visible_in_list, archived_at, created_at, updated_at, created_by'
          )
          .eq('org_id', orgId)
          .is('archived_at', null)
          .order('position', { ascending: true }),
        supabase
          .from('work_item_property_values')
          .select(
            'id, org_id, work_item_id, property_def_id, value, created_at, created_by, updated_at, updated_by'
          )
          .eq('work_item_id', workItemId)
      ]);
      if (cancelled) return;
      const err = defsRes.error?.message ?? valsRes.error?.message ?? null;
      if (err) {
        setLoad({ kind: 'error', message: err });
        return;
      }
      const values = new Map<string, WorkItemPropertyValueRow>();
      for (const row of (valsRes.data ?? []) as WorkItemPropertyValueRow[]) {
        values.set(row.property_def_id, row);
      }
      setLoad({
        kind: 'ready',
        defs: (defsRes.data ?? []) as WorkItemPropertyDefRow[],
        values
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [workItemId, orgId]);

  // Re-fetch ONLY this work item's property values and reconcile them into the
  // already-loaded panel (defs are unchanged by a value write). Used by the
  // Realtime subscription so an external/agent property edit appears inline
  // without a reopen. Reconciles to server truth; pendingCommits guards it from
  // fighting the user's own optimistic write.
  const refreshValues = useCallback(async () => {
    if (pendingCommitsRef.current > 0) {
      refreshQueuedRef.current = true;
      return;
    }
    const { data, error } = await supabase
      .from('work_item_property_values')
      .select(
        'id, org_id, work_item_id, property_def_id, value, created_at, created_by, updated_at, updated_by'
      )
      .eq('org_id', orgId)
      .eq('work_item_id', workItemId);
    if (error) {
      onError(error.message);
      return;
    }
    const values = new Map<string, WorkItemPropertyValueRow>();
    for (const row of (data ?? []) as WorkItemPropertyValueRow[]) {
      values.set(row.property_def_id, row);
    }
    setLoad((prev) => {
      if (prev.kind !== 'ready') return prev;
      return { ...prev, values };
    });
  }, [workItemId, orgId, onError]);

  // Live property refresh: subscribe to work_item_property_values rows for THIS
  // work item (FK filter work_item_id=eq.<workItemId>). Any external/agent write
  // (INSERT/UPDATE) triggers a debounced re-fetch — the same Supabase Realtime
  // mechanism that already refreshes the title/body for the open row.
  useRealtimeRowReload({
    table: 'work_item_property_values',
    filterColumn: 'work_item_id',
    rowId: workItemId || null,
    onChange: () => {
      void refreshValues();
    }
  });

  const commitValue = useCallback(
    async (defId: string, value: unknown) => {
      pendingCommitsRef.current += 1;
      const { data, error } = await supabase
        .from('work_item_property_values')
        .upsert(
          {
            work_item_id: workItemId,
            property_def_id: defId,
            org_id: orgId,
            value: value as never
          },
          { onConflict: 'work_item_id,property_def_id' }
        )
        .select(
          'id, org_id, work_item_id, property_def_id, value, created_at, created_by, updated_at, updated_by'
        )
        .single();
      pendingCommitsRef.current -= 1;
      if (pendingCommitsRef.current === 0 && refreshQueuedRef.current) {
        refreshQueuedRef.current = false;
        void refreshValues();
      }
      if (error) {
        onError(error.message);
        return;
      }
      setLoad((prev) => {
        if (prev.kind !== 'ready') return prev;
        const next = new Map(prev.values);
        next.set(defId, data as WorkItemPropertyValueRow);
        return { ...prev, values: next };
      });
    },
    [workItemId, orgId, onError, refreshValues]
  );

  const commitStatus = async (next: string) => {
    onStatusChange(next);
    const { error } = await supabase.rpc('set_work_item_status', {
      p_work_item_id: workItemId,
      p_status: next
    });
    if (error) {
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitDue = async (next: string | null) => {
    onDueDateChange(next);
    const { error } = await supabase.rpc('set_work_item_dates', {
      p_work_item_id: workItemId,
      p_start_date: workItemStartDate,
      p_end_date: workItemEndDate,
      p_due_date: next
    });
    if (error) {
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitStart = async (next: string | null) => {
    if (next && workItemEndDate && next > workItemEndDate) {
      setDateError('Start date cannot be after end date.');
      return;
    }
    setDateError(null);
    const previous = workItemStartDate;
    onStartDateChange(next);
    const { error } = await supabase.rpc('set_work_item_dates', {
      p_work_item_id: workItemId,
      p_start_date: next,
      p_end_date: workItemEndDate,
      p_due_date: workItemDueDate
    });
    if (error) {
      onStartDateChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitEnd = async (next: string | null) => {
    if (next && workItemStartDate && next < workItemStartDate) {
      setDateError('End date must be on or after start date.');
      return;
    }
    setDateError(null);
    const previous = workItemEndDate;
    onEndDateChange(next);
    const { error } = await supabase.rpc('set_work_item_dates', {
      p_work_item_id: workItemId,
      p_start_date: workItemStartDate,
      p_end_date: next,
      p_due_date: workItemDueDate
    });
    if (error) {
      onEndDateChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitParent = async (next: string | null) => {
    const previous = parentWorkItemId;
    onParentChange(next);
    const { error } = await supabase.rpc('set_work_item_parent', {
      p_work_item_id: workItemId,
      p_parent_id: next
    });
    if (error) {
      onParentChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const commitProject = async (next: string | null) => {
    const previous = workItemProjectId;
    onProjectChange(next);
    const { error } = await supabase.rpc('set_work_item_project', {
      p_work_item_id: workItemId,
      p_project_id: next
    });
    if (error) {
      onProjectChange(previous);
      onError(error.message);
      return;
    }
    onActivityChange?.();
  };

  const togglePicker = () => setPickerOpen((v) => !v);

  const pickDef = (defId: string) => {
    setPickerOpen(false);
    void commitValue(defId, null);
  };

  const typeLabel = TYPE_LABEL[workItemType] ?? workItemType;
  const stages = statusStages;
  const statusOptions: SelectOption[] = stages.map((s) => ({
    value: s,
    label: s
  }));
  if (!stages.includes(workItemStatus) && workItemStatus) {
    statusOptions.unshift({ value: workItemStatus, label: workItemStatus });
  }

  const epicSelectOptions: SelectOption[] = [
    { value: NO_EPIC_VALUE, label: '— No epic —' },
    ...epicOptions.map((e) => ({ value: e.id, label: e.name || '(untitled)' }))
  ];
  const epicSelectValue = parentWorkItemId ?? NO_EPIC_VALUE;

  const projectSelectOptions: SelectOption[] = [
    { value: NO_PROJECT_VALUE, label: '— No project —' },
    ...projects.map((p) => ({ value: p.id, label: p.name }))
  ];
  const projectSelectValue = workItemProjectId ?? NO_PROJECT_VALUE;

  // Wrap rows + footer in <Card> by default; the InspectorTabs caller
  // passes `naked` to skip the chrome (the tab body supplies its own
  // border + pad). Both branches share the same row + picker children.
  const wrap = (rows: ReactNode, foot: ReactNode) => {
    if (naked) {
      return (
        <div
          className="wid-properties-naked"
          data-testid="work-item-detail-properties"
          data-state={load.kind}
          aria-label="Work item properties"
        >
          <div className="wid-properties-rows">{rows}</div>
          <div className="wid-properties-naked-footer">{foot}</div>
        </div>
      );
    }
    return (
      <Card
        data-testid="work-item-detail-properties"
        data-state={load.kind}
        aria-label="Work item properties"
      >
        <Card.Head title="Properties" />
        <Card.Body className="wid-properties-rows">{rows}</Card.Body>
        <Card.Foot className="wid-properties-footer">{foot}</Card.Foot>
      </Card>
    );
  };

  const rows = (
    <>
        <div
          className="wid-properties-row"
          data-testid="work-item-detail-properties-row-type"
        >
          <MonoLabel size="sm" tracking="wider" className="wid-properties-label">
            Type
          </MonoLabel>
          <div
            className="wid-properties-value wid-properties-readonly"
            title={TYPE_LOCK_TOOLTIP}
          >
            {typeLabel}
          </div>
        </div>

        {workItemType === 'epic' ? null : workItemType === 'subtask' ? (
          <div
            className="wid-properties-row"
            data-testid="work-item-detail-properties-row-parent"
          >
            <MonoLabel
              size="sm"
              tracking="wider"
              className="wid-properties-label"
            >
              Parent
            </MonoLabel>
            <div className="wid-properties-value wid-properties-readonly">
              {parentName ?? '—'}
            </div>
          </div>
        ) : (
          <div
            className="wid-properties-row"
            data-testid="work-item-detail-properties-row-epic"
          >
            <MonoLabel
              size="sm"
              tracking="wider"
              className="wid-properties-label"
            >
              Epic
            </MonoLabel>
            <div className="wid-properties-value">
              {epicOptions.length === 0 ? (
                <div
                  className="wid-properties-readonly"
                  data-testid="work-item-detail-properties-epic-empty"
                >
                  No epics in this org yet. Create one from Work Items first.
                </div>
              ) : (
                <Select
                  id="work-item-detail-properties-epic"
                  label="Epic"
                  hideLabel
                  options={epicSelectOptions}
                  value={epicSelectValue}
                  onChange={(e) => {
                    const v = e.target.value;
                    void commitParent(v === NO_EPIC_VALUE ? null : v);
                  }}
                  data-testid="work-item-detail-properties-epic-select"
                />
              )}
            </div>
          </div>
        )}

        <div
          className="wid-properties-row"
          data-testid="work-item-detail-properties-row-project"
        >
          <MonoLabel size="sm" tracking="wider" className="wid-properties-label">
            Project
          </MonoLabel>
          <div className="wid-properties-value">
            {isChildItem ? (
              <div
                className="wid-properties-readonly"
                data-testid="work-item-detail-properties-project-inherited"
                title="A child work item inherits its project from its parent."
              >
                {inheritedProjectName
                  ? `${inheritedProjectName} (inherited from epic)`
                  : '— No project —'}
              </div>
            ) : projects.length === 0 ? (
              <div
                className="wid-properties-readonly"
                data-testid="work-item-detail-properties-project-empty"
              >
                No projects in this org yet. Create one from Projects first.
              </div>
            ) : (
              <Select
                id="work-item-detail-properties-project"
                label="Project"
                hideLabel
                options={projectSelectOptions}
                value={projectSelectValue}
                onChange={(e) => {
                  const v = e.target.value;
                  void commitProject(v === NO_PROJECT_VALUE ? null : v);
                }}
                data-testid="work-item-detail-properties-project-select"
              />
            )}
          </div>
        </div>

        <div
          className="wid-properties-row"
          data-testid="work-item-detail-properties-row-status"
        >
          <MonoLabel size="sm" tracking="wider" className="wid-properties-label">
            Status
          </MonoLabel>
          <div className="wid-properties-value">
            <Select
              id="work-item-detail-properties-status"
              label="Status"
              hideLabel
              options={statusOptions}
              value={workItemStatus}
              onChange={(e) => void commitStatus(e.target.value)}
              data-testid="work-item-detail-properties-status-select"
            />
          </div>
        </div>

        <BuildOrderRow
          workItemId={workItemId}
          orgId={orgId}
          value={workItemBuildOrder}
          onBuildOrderChange={onBuildOrderChange}
          onError={onError}
        />

        {workItemType === 'epic' ? (
          <>
            <div
              className="wid-properties-row"
              data-testid="work-item-detail-properties-row-start"
            >
              <MonoLabel
                size="sm"
                tracking="wider"
                className="wid-properties-label"
              >
                Start
              </MonoLabel>
              <div className="wid-properties-value">
                <DateField
                  id="work-item-detail-properties-start"
                  label="Start"
                  hideLabel
                  value={workItemStartDate}
                  onChange={(next) => void commitStart(next)}
                  testId="work-item-detail-properties-start-input"
                />
              </div>
            </div>

            <div
              className="wid-properties-row"
              data-testid="work-item-detail-properties-row-end"
            >
              <MonoLabel
                size="sm"
                tracking="wider"
                className="wid-properties-label"
              >
                End
              </MonoLabel>
              <div className="wid-properties-value">
                <DateField
                  id="work-item-detail-properties-end"
                  label="End"
                  hideLabel
                  value={workItemEndDate}
                  onChange={(next) => void commitEnd(next)}
                  testId="work-item-detail-properties-end-input"
                />
              </div>
            </div>

            {dateError ? (
              <div
                className="wid-properties-error"
                data-testid="work-item-detail-properties-date-error"
                role="alert"
              >
                {dateError}
              </div>
            ) : null}
          </>
        ) : null}

        <div
          className="wid-properties-row"
          data-testid="work-item-detail-properties-row-due"
        >
          <MonoLabel size="sm" tracking="wider" className="wid-properties-label">
            Due
          </MonoLabel>
          <div className="wid-properties-value">
            <DateField
              id="work-item-detail-properties-due"
              label="Due"
              hideLabel
              value={workItemDueDate}
              onChange={(next) => void commitDue(next)}
              testId="work-item-detail-properties-due-input"
            />
          </div>
        </div>

        {load.kind === 'loading' ? (
          <>
            <Skeleton width="100%" height={28} />
            <Skeleton width="100%" height={28} />
          </>
        ) : null}
        {load.kind === 'error' ? (
          <div className="wid-properties-error">{load.message}</div>
        ) : null}
        {load.kind === 'ready'
          ? load.defs.map((def) => (
              <CustomPropertyRow
                key={def.id}
                def={def}
                value={load.values.get(def.id) ?? null}
                onCommit={commitValue}
              />
            ))
          : null}
    </>
  );

  const foot = (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={togglePicker}
        data-testid="work-item-detail-properties-add"
        aria-expanded={pickerOpen}
      >
        + Add property
      </Button>
      {pickerOpen && load.kind === 'ready' ? (
        <ul
          className="wid-properties-picker"
          data-testid="work-item-detail-properties-picker"
        >
          {load.defs.length === 0 ? (
            <li className="wid-properties-picker-empty">
              No properties in the Work Item Property Library yet.
            </li>
          ) : (
            load.defs.map((def) => (
              <li key={def.id}>
                <button
                  type="button"
                  className="wid-properties-picker-item"
                  onClick={() => pickDef(def.id)}
                  data-testid={`work-item-detail-properties-picker-item-${def.key}`}
                >
                  {def.label}
                </button>
              </li>
            ))
          )}
        </ul>
      ) : null}
      <p
        className="wid-properties-legend"
        data-testid="work-item-detail-properties-legend"
      >
        All properties come from the Work Item Property Library.
      </p>
    </>
  );

  return wrap(rows, foot);
}

interface BuildOrderRowProps {
  workItemId: string;
  orgId: string;
  /** The stored `work_items.build_order` (null = unordered, sorts last). */
  value: number | null;
  onBuildOrderChange: (next: number | null) => void;
  onError: (message: string) => void;
}

/**
 * The "Build order" system row — `work_items.build_order`, the item's 1-based
 * position among its SIBLINGS (items sharing its parent; each sibling group
 * numbers independently — 05-project-hub-build-order). Edits are optimistic
 * (`onBuildOrderChange` on each valid keystroke), debounced, blur-flushed, and
 * commit through the SAME direct, org-scoped `work_items` update path the Build
 * Order section's drag uses — no `set_work_item_*` RPC covers build_order, and
 * (like a drag reorder) no audit row is written. Empty ⇒ NULL; a non-finite
 * draft is refused, never written.
 */
function BuildOrderRow({
  workItemId,
  orgId,
  value,
  onBuildOrderChange,
  onError
}: BuildOrderRowProps) {
  const toDraft = (v: number | null) => (v === null ? '' : String(v));
  const [draft, setDraft] = useState(toDraft(value));
  const draftRef = useRef(draft);
  // The last SERVER-CONFIRMED value — the revert target on a failed write
  // (the lifted prop is optimistic, so it cannot serve as the baseline).
  const baselineRef = useRef(value);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // External reconcile (a realtime/agent edit re-feeding the prop): adopt it
  // only while no local edit is pending.
  useEffect(() => {
    if (debounceRef.current) return;
    if (value === baselineRef.current) return;
    baselineRef.current = value;
    setDraft(toDraft(value));
    draftRef.current = toDraft(value);
  }, [value]);

  useEffect(
    () => () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  /** '' ⇒ null; a finite number ⇒ that number; anything else ⇒ refused. */
  const parse = (raw: string): { ok: true; val: number | null } | { ok: false } => {
    const trimmed = raw.trim();
    if (trimmed === '') return { ok: true, val: null };
    const n = Number(trimmed);
    return Number.isFinite(n) ? { ok: true, val: n } : { ok: false };
  };

  const flush = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const parsed = parse(draftRef.current);
    if (!parsed.ok) return;
    const next = parsed.val;
    const previous = baselineRef.current;
    if (next === previous) return;
    void (async () => {
      const { error } = await supabase
        .from('work_items')
        .update({ build_order: next })
        .eq('id', workItemId)
        .eq('org_id', orgId);
      if (error) {
        onBuildOrderChange(previous);
        setDraft(toDraft(previous));
        draftRef.current = toDraft(previous);
        onError(error.message);
        return;
      }
      baselineRef.current = next;
    })();
  };

  const schedule = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      flush();
    }, COMMIT_DEBOUNCE_MS);
  };

  return (
    <div
      className="wid-properties-row"
      data-testid="work-item-detail-properties-row-buildorder"
    >
      <MonoLabel size="sm" tracking="wider" className="wid-properties-label">
        Build order
      </MonoLabel>
      <div className="wid-properties-value">
        <TextField
          id="work-item-detail-properties-buildorder"
          label="Build order"
          hideLabel
          type="number"
          value={draft}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            draftRef.current = next;
            const parsed = parse(next);
            if (parsed.ok) onBuildOrderChange(parsed.val);
            schedule();
          }}
          onBlur={() => {
            if (debounceRef.current) flush();
          }}
          data-testid="work-item-detail-properties-buildorder-input"
        />
        <p className="wid-properties-hint">Position among its siblings (1 = first).</p>
      </div>
    </div>
  );
}

interface CustomPropertyRowProps {
  def: WorkItemPropertyDefRow;
  value: WorkItemPropertyValueRow | null;
  onCommit: (defId: string, value: unknown) => Promise<void>;
}

function readScalar(v: WorkItemPropertyValueRow['value'] | undefined): string {
  if (v == null) return '';
  if (typeof v === 'string') return v;
  if (typeof v === 'number') return String(v);
  return '';
}

function CustomPropertyRow({ def, value, onCommit }: CustomPropertyRowProps) {
  const persisted = readScalar(value?.value);
  const [draft, setDraft] = useState(persisted);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const draftRef = useRef(persisted);

  useEffect(() => {
    if (debounceRef.current) return;
    if (draftRef.current === persisted) return;
    setDraft(persisted);
    draftRef.current = persisted;
  }, [persisted]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const coerce = (raw: string): unknown => {
    const trimmed = raw.trim();
    if (trimmed === '') return null;
    if (def.kind === 'number') {
      const n = Number(trimmed);
      return Number.isFinite(n) ? n : null;
    }
    return trimmed;
  };

  const flush = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
    }
    const next = coerce(draftRef.current);
    const current = value?.value ?? null;
    if (next === current) return;
    void onCommit(def.id, next);
  };

  const schedule = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      debounceRef.current = null;
      flush();
    }, COMMIT_DEBOUNCE_MS);
  };

  const inputType = def.kind === 'number' ? 'number' : 'text';
  const inputId = `work-item-detail-properties-${def.key}-input`;

  return (
    <div
      className="wid-properties-row"
      data-testid={`work-item-detail-properties-row-${def.key}`}
      data-property-kind={def.kind}
    >
      <MonoLabel size="sm" tracking="wider" className="wid-properties-label">
        {def.label}
      </MonoLabel>
      <div className="wid-properties-value">
        <TextField
          id={inputId}
          label={def.label}
          hideLabel
          type={inputType}
          value={draft}
          onChange={(e) => {
            const next = e.target.value;
            setDraft(next);
            draftRef.current = next;
            schedule();
          }}
          onBlur={() => {
            if (debounceRef.current) {
              clearTimeout(debounceRef.current);
              debounceRef.current = null;
              flush();
            }
          }}
          data-testid={`work-item-detail-properties-${def.key}-input`}
        />
      </div>
    </div>
  );
}
